import i18n from "../../../../utils/i18n";

const formatNavAddressLine = (nav) => {
  if (!nav) return "";
  const parts = [
    nav.zip,
    nav.city,
    nav.place_name,
    nav.place_category,
    nav.number,
    nav.building,
    nav.staircase,
    nav.floor,
    nav.door,
  ].filter((p) => p !== undefined && p !== null && String(p).trim() !== "");
  return parts.join(" ").trim();
};

const pickNamedValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    const firstNamedItem = value.find(
      (item) => typeof item === "string" || typeof item?.name === "string",
    );
    if (typeof firstNamedItem === "string") return firstNamedItem.trim();
    if (typeof firstNamedItem?.name === "string") return firstNamedItem.name.trim();
    return "";
  }
  if (typeof value?.name === "string") return value.name.trim();
  return "";
};

export const getCegjelzoCompanyName = (companyData) => {
  if (!companyData) return "";
  return (
    pickNamedValue(companyData.name) ||
    pickNamedValue(companyData.long_name) ||
    pickNamedValue(companyData.short_name) ||
    ""
  );
};

/**
 * Name candidates for `companytype` parsing (COMPANIES registry).
 * Order matters: short_name → name → long_name.
 * short_name usually carries the legal-form suffix (e.g. "Example Kft.");
 * long_name is often the spelled-out form without a parseable suffix
 * (e.g. "Example Korlátolt Felelősségű Társaság").
 * Display uses a different priority — see `getCegjelzoCompanyName`.
 */
export const getCegjelzoCompanyNameCandidates = (companyData) => {
  if (!companyData) return [];
  const candidates = [
    pickNamedValue(companyData.short_name),
    pickNamedValue(companyData.name),
    pickNamedValue(companyData.long_name),
    getCegjelzoCompanyName(companyData),
  ].filter(Boolean);
  return [...new Set(candidates)];
};

export const getCegjelzoTaxNumber = (companyData) => {
  if (!companyData) return "";

  if (companyData.full_tax_number) {
    return String(companyData.full_tax_number).trim();
  }

  const tax = companyData.tax_number;
  if (typeof tax === "string" || typeof tax === "number") {
    return String(tax).trim();
  }

  if (Array.isArray(tax) && tax.length > 0) {
    const first = tax[0];
    if (typeof first === "string") return first.trim();
    if (first?.tax_number) return String(first.tax_number).trim();
  }

  return "";
};

export const getCegjelzoHeadquarters = (companyData) => {
  if (!companyData) return "";

  const fromNav = formatNavAddressLine(companyData.nav_address);
  if (fromNav) return fromNav;

  const address = companyData.address;
  if (typeof address === "string" && address.trim()) {
    return address.trim();
  }

  if (Array.isArray(address) && address.length > 0) {
    const first = address[0];
    if (typeof first === "string") return first.trim();
    if (first?.address) return String(first.address).trim();
  }

  const registeredAddress = companyData.registered_address ?? companyData.headquarters;
  if (typeof registeredAddress === "string" && registeredAddress.trim()) {
    return registeredAddress.trim();
  }

  return "";
};

const getMainActivityLabelFromList = (activities) => {
  if (!Array.isArray(activities) || activities.length === 0) return "";

  const sorted = [...activities].sort((a, b) => {
    const aMain = a?.is_main_activity === true ? 0 : 1;
    const bMain = b?.is_main_activity === true ? 0 : 1;
    return aMain - bMain;
  });

  const activity = sorted[0];
  return (
    activity?.teaor_name ||
    activity?.nace_name ||
    activity?.teaor_value ||
    activity?.name ||
    ""
  ).trim();
};

export const getCegjelzoMainActivity = (companyData) => {
  if (!companyData) return "";

  const fromActivities = getMainActivityLabelFromList(companyData.main_activities);
  if (fromActivities) return fromActivities;

  const directFields = [
    companyData.main_activity,
    companyData.main_teaor,
    companyData.teaor,
  ];
  for (const field of directFields) {
    if (typeof field === "string" && field.trim()) {
      const trimmed = field.trim();
      const nameMatch = trimmed.match(/^\d[\d.\s']*\s+(.+)$/);
      return nameMatch ? nameMatch[1].trim() : trimmed;
    }
  }

  const nestedInfos = companyData.cegjelzoInfos ?? companyData.details?.cegjelzoInfos;
  if (nestedInfos?.main_activities) {
    const fromNested = getMainActivityLabelFromList(nestedInfos.main_activities);
    if (fromNested) return fromNested;
  }

  return "";
};

export const getCegjelzoDisplayFields = (companyData) => [
  {
    label: i18n.t("searchflow.cegjelzoselectedcompany"),
    value: getCegjelzoCompanyName(companyData),
  },
  { label: i18n.t("general.taxnumber"), value: getCegjelzoTaxNumber(companyData) },
  {
    label: i18n.t("searchflow.cegjelzoheadquarters"),
    value: getCegjelzoHeadquarters(companyData),
  },
  {
    label: i18n.t("searchflow.cegjelzomainactivity"),
    value: getCegjelzoMainActivity(companyData),
  },
];
